import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useTransition } from "react";
import { StyleSheet } from "react-native-unistyles";
import { useFocusEffect, useNavigation, useRouter } from "expo-router";
import { CameraList, CamerasTabHeader, useCameras } from "@src/features/cameras";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { getCameraKeyString, isLegacyAppCamera } from "@src/utils/key-parser";
import { useAlarmCacheForCameras } from "@src/services/api/hooks";
import { getCurrentCameraArmedState } from "@src/features/alarms/utils/alarm-utils";
import { isArmedFromArmingType } from "@src/features/schedules/utils/schedule-utils";
import { ScreenContainer, View } from "@src/components";
import { Text } from "@src/components/Text";
import { ActivityIndicator, Keyboard } from "react-native";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";
import type { SortOption } from "@src/features/clips/types/camera";
import { useTenant, getTenantDisplayName } from "@src/features/tenants";
import type { TextInput } from "react-native";
import Animated, { LinearTransition } from "react-native-reanimated";
import { useSiteWideDrawer } from "@src/components/AppDrawer/SiteWideDrawerProvider.native";

export default function OverviewScreen() {
  const navigation = useNavigation();
  const router = useRouter();
  const { tenants, selectedTenant } = useTenant();
  const { data: cameras, refetch: refetchCameras } = useCameras();
  const tenantId = selectedTenant?.id;
  const searchInputRef = useRef<TextInput>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>("site");
  const { openDrawer } = useSiteWideDrawer();
  const [alarmFilterVisual, setAlarmFilterVisual] = useState<"armed" | "disarmed" | null>(null);
  const [alarmFilter, setAlarmFilter] = useState<"armed" | "disarmed" | null>(null);
  const selectedSiteId = useSelectedSiteStore((s) => s.selectedSiteId);

  const tenantDisplayName = selectedTenant ? getTenantDisplayName(selectedTenant) : null;

  const siteGroups = useMemo(() => {
    if (!cameras) return [];
    return groupCamerasBySite(cameras, "site");
  }, [cameras]);

  const selectedSiteName = useMemo(() => {
    if (!selectedSiteId) return null;
    return siteGroups.find((g) => g.siteId === selectedSiteId)?.siteName ?? null;
  }, [selectedSiteId, siteGroups]);

  // Alarm filter: read cached alarm data (already fetched by each CameraListItem)
  const alarmCameraParams = useMemo(() => {
    if (!cameras) return [];
    return cameras
      .filter((c) => !isLegacyAppCamera(c))
      .map((c) => ({ cameraKey: getCameraKeyString(c), siteId: c.site.id }));
  }, [cameras]);

  const cachedAlarms = useAlarmCacheForCameras(alarmCameraParams);

  const cameraKeyFilter = useMemo(() => {
    if (!alarmFilter) return undefined;
    const now = new Date();
    const keys = new Set<string>();
    for (let i = 0; i < alarmCameraParams.length; i++) {
      const cached = cachedAlarms[i];
      if (!cached) continue;
      const armedState = getCurrentCameraArmedState(cached, now);
      const isArmed = armedState ? isArmedFromArmingType(armedState.armingType) : false;
      if ((alarmFilter === "armed" && isArmed) || (alarmFilter === "disarmed" && !isArmed)) {
        keys.add(alarmCameraParams[i].cameraKey);
      }
    }
    return keys;
  }, [alarmFilter, alarmCameraParams, cachedAlarms]);

  const [isPending, startTransition] = useTransition();

  const toggleAlarmFilter = useCallback(
    (filter: "armed" | "disarmed") => {
      const next = alarmFilterVisual === filter ? null : filter;
      setAlarmFilterVisual(next);
      startTransition(() => {
        setAlarmFilter(next);
      });
    },
    [alarmFilterVisual, startTransition],
  );

  const toggleSearch = useCallback(() => {
    setSearchExpanded((prev) => {
      if (prev) {
        setSearchQuery("");
        Keyboard.dismiss();
      }
      return !prev;
    });
  }, []);

  const handleHamburgerMenuPress = useCallback(() => {
    openDrawer();
  }, [openDrawer]);

  useFocusEffect(useCallback(() => void refetchCameras(), [refetchCameras]));
  useEffect(() => {
    if (tenantId) void refetchCameras();
  }, [tenantId, refetchCameras]);

  useLayoutEffect(() => navigation.setOptions({ headerShown: false }), [navigation]);

  return (
    <ScreenContainer edges={["top"]}>
      <CamerasTabHeader
        tenantDisplayName={tenantDisplayName}
        tenantsCount={tenants.length}
        onTenantPress={() => router.push("/settings/tenant-select")}
        selectedSiteName={selectedSiteName}
        onHamburgerMenuPress={handleHamburgerMenuPress}
        onSearchPress={toggleSearch}
        sortBy={sortBy}
        onSortChange={setSortBy}
        searchExpanded={searchExpanded}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        searchInputRef={searchInputRef}
        alarmFilter={alarmFilterVisual}
        onAlarmFilterToggle={toggleAlarmFilter}
      />
      <View style={styles.screen}>
        <Animated.View style={styles.listWrap} layout={LinearTransition.springify()}>
          {isPending ?
            <View flex={1} justifyContent="center" alignItems="center" gap="xl">
              <ActivityIndicator size="large" />
              <Text textStyle="sm/regular" color="text">
                Loading cameras...
              </Text>
            </View>
          : <CameraList
              key={tenantId ?? "no-tenant"}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              dockSearchToHeader
              suppressHeaderSearch
              cameraKeyFilter={cameraKeyFilter}
            />
          }
        </Animated.View>
      </View>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create((theme) => ({
  screen: { flex: 1, backgroundColor: theme.colors.background },
  listWrap: { flex: 1, minHeight: 0 },
}));
